import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  OnDestroy,
  OnInit,
  output,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { inject } from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import {
  DialogHeaderComponent,
  DialogFooterComponent,
  ButtonComponent,
  FormFieldComponent,
  MessageComponent,
} from '@akostm/orbit-ui';
import { TranslateModule } from '@ngx-translate/core';
import { signal } from '@angular/core';
import { emailRegexpValidator, oneOfFieldsRequiredValidator } from '../../../../validators';
import { ErrorStore } from '../../../../stores';
import { ParticipantKind } from '@clientwire/api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PhoneNumberUtil } from 'google-libphonenumber';

@Component({
  selector: 'shared-edit-participant-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DialogHeaderComponent,
    DialogFooterComponent,
    ButtonComponent,
    FormFieldComponent,
    TranslateModule,
    MessageComponent,
  ],
  templateUrl: './edit-participant-dialog.component.html',
  styleUrl: './edit-participant-dialog.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditParticipantDialogComponent implements OnDestroy, OnInit {
  readonly submitClicked = output<{ displayName: string; phoneNumber: string; email: string }>();
  readonly #errorStore = inject(ErrorStore);
  readonly #dialogRef = inject(DialogRef);
  readonly data: any = inject(DIALOG_DATA);
  readonly isSubmitting = signal(false);
  readonly editParticipantForm: FormGroup = new FormGroup({
    displayName: new FormControl<string | null>(null, [Validators.required]),
    phoneNumber: new FormControl<string | null>(null),
    email: new FormControl<string | null>(null),
  });
  readonly #errorLocations = ['editParticipant'];
  readonly errors = computed(() => this.#errorStore.getErrorsByLocations(this.#errorLocations));
  readonly role = computed(() => this.data.participant.kind);
  readonly participantKindEnum = ParticipantKind;
  readonly #destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.displayName.setValue(this.data.participant.displayName);
    if (this.role() === ParticipantKind.Client) {
      // Add validators
      this.email.setValidators([emailRegexpValidator()]);
      this.editParticipantForm.addValidators([
        oneOfFieldsRequiredValidator(['phoneNumber', 'email']),
      ]);

      // Set values
      this.phoneNumber.setValue(this.data.participant.phoneNumber);
      this.email.setValue(this.data.participant.email);

      this.phoneNumber?.valueChanges
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe((value) => {
          let formattedNumber = value;
          if (formattedNumber) {
            const phoneUtil = PhoneNumberUtil.getInstance();
            try {
              const number = phoneUtil.parse(value, 'AT');
              formattedNumber = phoneUtil.format(number, 0);
              this.phoneNumber.setValue(formattedNumber, { emitEvent: false });
            } catch (e) {
              // If parsing fails, do nothing
            }
          }

          if (this.displayName.pristine && value && !this.email.value) {
            this.displayName.setValue(formattedNumber);
          }
        });

      this.email?.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((value) => {
        if (this.displayName.pristine && value) {
          this.displayName.setValue(value);
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.#errorStore.clearErrorsByLocations(this.#errorLocations);
  }

  get displayName() {
    return this.editParticipantForm.get('displayName') as FormControl<string>;
  }

  get phoneNumber() {
    return this.editParticipantForm.get('phoneNumber') as FormControl<string>;
  }

  get email() {
    return this.editParticipantForm.get('email') as FormControl<string>;
  }

  onCancel(): void {
    this.#dialogRef.close();
  }

  onSubmit(): void {
    if (this.editParticipantForm.invalid) {
      this.editParticipantForm.markAllAsTouched();
      return;
    }
    this.isSubmitting.set(true);
    this.submitClicked.emit(this.editParticipantForm.value);
  }
}
